export const UNIVERSITY_1_CONSTANTS = {
    DEFAULT_ROW: {
        id: "1", // University ID is always 1
        name: "",
        uni_code: "",
        source: "",
        desktop_promotional_banner: "",
        mobile_promotional_banner: "",
        mobile_promotional_icon: "",
        enable_promotional_banner: false,
        promotional_blink_interval_seconds: "",
        mobile_whatsapp_icon: "",
        mobile_phone_icon: "",
        whatsapp_link: "",
        phone_link: "",
        animation_color: "#000000",
        favicon: "",
        chatbot_provider: "",
        lead_crm_provider: "erp",
        non_verified_sweep_enabled: false,
        non_verified_grace_minutes: "",
        non_verified_media: "",
        non_verified_campaign: "",
        default_sitemap_lastmod: "",
        legal_name: "",
        social_links: "",
        address_street: "",
        address_locality: "",
        address_region: "",
        address_postal_code: "",
        address_country: "",
    },

    UNIVERSITY_FIELDS_CONFIG: [
        {
            fieldName: "name",
            label: "University Name",
            value: "",
            valueType: "string",
            filedType: "INPUT",
            placeholder: "Enter university name",
        },
        {
            fieldName: "uni_code",
            label: "University Code",
            value: "",
            valueType: "string",
            filedType: "INPUT",
            placeholder: "Enter university code",
        },
        {
            fieldName: "source",
            label: "Source",
            value: "",
            valueType: "string",
            filedType: "INPUT",
            placeholder: "Enter source",
        },
        {
            fieldName: "desktop_promotional_banner",
            label: "Desktop Promotional Banner",
            value: "",
            valueType: "string",
            filedType: "FILE",
            placeholder: "Upload desktop promotional banner",
            required: false,
        },
        {
            fieldName: "mobile_promotional_banner",
            label: "Mobile Promotional Banner",
            value: "",
            valueType: "string",
            filedType: "FILE",
            placeholder: "Upload mobile promotional banner",
            required: false,
        },
        {
            fieldName: "mobile_promotional_icon",
            label: "Mobile Promotional Icon",
            value: "",
            valueType: "string",
            filedType: "FILE",
            placeholder: "Upload mobile promotional icon",
            required: false,
        },
        {
            fieldName: "promotional_banner_alt",
            label: "Promotional Banner Alt Text",
            value: "",
            valueType: "string",
            filedType: "INPUT",
            placeholder: "Enter alt text for promotional banner",
            required: false,
        },
        {
            fieldName: "promotional_blink_interval_seconds",
            label: "Gap Between Icon Blinks (seconds)",
            value: "",
            valueType: "number",
            filedType: "INPUT",
            placeholder: "e.g. 2 (blank = 2 seconds)",
            description:
                "Quiet pause between two blinks of the floating promotional icon, in seconds. The blink itself always takes the same 1.5 seconds — a larger number blinks less often, it does not slow the blink down. Applies to every page on this university. Leave blank for a 2 second gap.",
            required: false,
        },
        {
            fieldName: "enable_promotional_banner",
            label: "Enable Promotional Banner",
            value: false,
            valueType: "boolean",
            filedType: "TOGGLE",
            placeholder: "",
        },
        {
            fieldName: "mobile_whatsapp_icon",
            label: "Mobile WhatsApp Icon",
            value: "",
            valueType: "string",
            filedType: "FILE",
            placeholder: "Upload mobile WhatsApp icon",
            required: false,
        },
        {
            fieldName: "mobile_phone_icon",
            label: "Mobile Phone Icon",
            value: "",
            valueType: "string",
            filedType: "FILE",
            placeholder: "Upload mobile phone icon",
            required: false,
        },
        {
            fieldName: "whatsapp_link",
            label: "WhatsApp Link",
            value: "",
            valueType: "string",
            filedType: "INPUT",
            placeholder: "Enter WhatsApp link (e.g., https://wa.me/1234567890)",
            required: false,
        },
        {
            fieldName: "phone_link",
            label: "Phone Link",
            value: "",
            valueType: "string",
            filedType: "INPUT",
            placeholder: "Enter phone link (e.g., tel:+1234567890)",
            required: false,
        },
        {
            fieldName: "animation_color",
            label: "Animation Color",
            value: "#000000",
            valueType: "string",
            filedType: "COLOR",
            placeholder: "Select animation color",
            required: false,
            defaultValue: "#000000",
        },
        {
            fieldName: "favicon",
            label: "Favicon",
            value: "",
            valueType: "string",
            filedType: "FILE",
            placeholder: "Upload favicon (.ico)",
            required: false,
        },
        {
            fieldName: "chatbot_provider",
            label: "Chatbot Provider",
            value: "none",
            valueType: "string",
            filedType: "SELECT",
            placeholder: "Select chatbot provider",
            required: false,
            options: [
                { value: "none", label: "None" },
                { value: "kenyt", label: "Kenyt" },
                { value: "tawk", label: "Tawk.to" },
                { value: "freshdesk", label: "Freshdesk (Freshchat)" },
                { value: "upgrad_chatbot", label: "upGrad Chatbot" },
            ],
        },
        {
            /**
             * Which CRM receives every lead this university drops. The existing ERP is the
             * default and needs no configuration; "New ERP" additionally requires the new
             * ERP's server configuration, and the save is REJECTED by the API with an
             * explanation when that is absent (so this can never silently do nothing).
             *
             * `confirmOnChange` makes Form_1 block the save behind a confirmation dialog —
             * this decides where every future lead lands, so it must not be changed by a
             * mis-click or an unnoticed edit. Keep the 5-minute figure in `note` in step with
             * LEAD_CRM_PROVIDER_CACHE_TTL_MS in server/constants/leadCrm.js.
             */
            fieldName: "lead_crm_provider",
            label: "Lead CRM",
            value: "erp",
            valueType: "string",
            filedType: "SELECT",
            placeholder: "Select where leads are sent",
            required: false,
            defaultValue: "erp",
            options: [
                { value: "erp", label: "ERP (existing)" },
                { value: "new-erp", label: "New ERP" },
            ],
            description:
                "Where every lead from this university is sent — the existing ERP (default) or the new ERP lead-drop API. Applies to form submissions and to the background non-verified sweep alike. If the new ERP is not configured on this server, saving it is refused and leads keep going to the existing ERP.",
            confirmOnChange: {
                message:
                    "This changes where EVERY new lead is delivered, including background drops. Leads already sent stay where they are — nothing is migrated or re-sent.",
                note: "It takes effect on this server immediately and on all other running servers within 5 minutes.",
                optionLabels: {
                    erp: "ERP (existing)",
                    "new-erp": "New ERP",
                },
            },
        },
        {
            fieldName: "non_verified_sweep_enabled",
            label: "Enable Non-Verified Sweep",
            value: false,
            valueType: "boolean",
            filedType: "TOGGLE",
            placeholder: "",
            description:
                "Master switch for the background non-verified lead-drop sweep. When OFF, the sweep does nothing for this university (no leads are auto-dropped). When ON, abandoned/non-verified leads are dropped to the ERP after the grace window using the media/campaign below.",
            required: false,
        },
        {
            fieldName: "non_verified_grace_minutes",
            label: "Non-Verified Grace Window (minutes)",
            value: "",
            valueType: "number",
            filedType: "INPUT",
            placeholder: "e.g. 15 (blank = server default)",
            description:
                "How many minutes an abandoned lead sits before the sweep drops it — and how long a lead stuck mid-drop waits before being retried (same value). Leave blank to use the server default. Applies on the next sweep tick after you save (no restart).",
            required: false,
        },
        {
            fieldName: "non_verified_media",
            label: "Non-Verified Media",
            value: "",
            valueType: "string",
            filedType: "INPUT",
            placeholder: "e.g. OTP_nonVerified",
            description:
                "ERP \"media\" value sent when the background sweep drops a lead that never verified its phone (OTP). Leave blank to fall back to \"OTP_nonVerified\".",
            required: false,
        },
        {
            fieldName: "non_verified_campaign",
            label: "Non-Verified Campaign",
            value: "",
            valueType: "string",
            filedType: "INPUT",
            placeholder: "e.g. NV-Campaign",
            description:
                "ERP \"campaign\" value sent for non-verified lead drops (varies per instance). Leave blank to keep the lead's own captured campaign.",
            required: false,
        },
        {
            fieldName: "default_sitemap_lastmod",
            label: "Default Sitemap Last Modified",
            value: "",
            valueType: "text",
            filedType: "DATETIME",
            placeholder: "",
            description:
                "Global fallback <lastmod> for sitemap.xml, used for any page that has no per-page value set (and the home page). Leave blank to omit <lastmod> from those entries entirely.",
            required: false,
        },
        {
            fieldName: "legal_name",
            label: "Legal Name",
            value: "",
            valueType: "string",
            filedType: "INPUT",
            placeholder: "Registered/legal name for Organization schema (optional)",
            required: false,
        },
        {
            fieldName: "social_links",
            label: "Social Profile URLs",
            value: "",
            valueType: "string",
            filedType: "INPUT",
            placeholder: "Comma-separated URLs, e.g. LinkedIn, X, Wikipedia (optional)",
            required: false,
        },
        {
            fieldName: "address_street",
            label: "Street Address",
            value: "",
            valueType: "string",
            filedType: "INPUT",
            placeholder: "Street address (optional)",
            required: false,
        },
        {
            fieldName: "address_locality",
            label: "City / Locality",
            value: "",
            valueType: "string",
            filedType: "INPUT",
            placeholder: "City or locality (optional)",
            required: false,
        },
        {
            fieldName: "address_region",
            label: "State / Region",
            value: "",
            valueType: "string",
            filedType: "INPUT",
            placeholder: "State or region (optional)",
            required: false,
        },
        {
            fieldName: "address_postal_code",
            label: "Postal Code",
            value: "",
            valueType: "string",
            filedType: "INPUT",
            placeholder: "Postal / PIN code (optional)",
            required: false,
        },
        {
            fieldName: "address_country",
            label: "Country",
            value: "",
            valueType: "string",
            filedType: "INPUT",
            placeholder: "Country, e.g. India (optional)",
            required: false,
        },
    ],
}

